// Measuring type on the prep screen the way the stamper measures it.
//
// A multi-line box wraps its text at the box's width (see
// shared/esign/multiline-text.ts). The stamper wraps with pdf-lib's
// Helvetica metrics; the screen has no pdf-lib, so it measures with a
// canvas set in Helvetica (Arial where Helvetica is not installed — the
// same metrics to within a hair, which is why Arial exists). Both sides
// hand the SAME wrapper the same widths, so what the sender sees wrap on
// screen is what prints.
//
// One shared canvas, created lazily: measuring a forty-line section on
// every keystroke must not allocate.

import type { MeasureText } from "@shared/esign/multiline-text";

/** The face the screen draws and measures multi-line text in. Kept
 *  identical between the two so the visible wrap is the measured one. */
export const MULTILINE_SCREEN_FONT_FAMILY = "Helvetica, Arial, sans-serif";

let context: CanvasRenderingContext2D | null | undefined;

function measuringContext(): CanvasRenderingContext2D | null {
  if (context !== undefined) return context;
  try {
    const canvas = document.createElement("canvas");
    context = canvas.getContext("2d");
  } catch {
    context = null;
  }
  return context;
}

/** Width of `text` at `fontSizePt`, in the overlay's own units (screen
 *  px == PDF pt on the prep screen, which renders pages at scale 1).
 *  Falls back to a plain per-character estimate when no canvas is
 *  available (a test runner, a very old browser), so wrapping still
 *  happens rather than throwing. */
export const measureScreenText: MeasureText = (text, fontSizePt) => {
  const ctx = measuringContext();
  if (!ctx) return text.length * fontSizePt * 0.5;
  ctx.font = `${fontSizePt}px ${MULTILINE_SCREEN_FONT_FAMILY}`;
  return ctx.measureText(text).width;
};
